import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';

import { isBrowser, venture, restApiUrl } from '../../config';
import ContentfulRichText from '../../components/ContentfulRichText';
import { callAPI, formatMaskedPhoneNumberDisplay, getBrandId, getErrorMessage } from './utils';

const GENERIC_ERROR_MESSAGE = 'Something went wrong, please try again.';

// Client side validations finds the names of the following fields
// in detailsGatheringFields and/or verifyOtpFields and attaches to
// React Hook Form's validate function.
const CLIENT_SIDE_VALIDATIONS = {
  email: {
    isValidEmail: (value) =>
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) || 'Email address is invalid',
  },
  dob: {
    isNineteenOrOlder: (value) => {
      const parts = (value ?? '').split('/');

      const dob = new Date(value);

      const nineteenYearsAgo = new Date();

      nineteenYearsAgo.setFullYear(nineteenYearsAgo.getFullYear() - 19);

      const isValid = parts.length === 3 && parts.every(Boolean) && !isNaN(dob) && dob < nineteenYearsAgo;

      return isValid || 'You must be over 19 to play with us';
    },
  },
};

const PreRegistrationForm = isBrowser
  ? loadable(() => import('@albatross/common-components/dist/components/PreRegistrationForm'))
  : () => null;

const ConfirmDialog = isBrowser
  ? loadable(() => import('@albatross/common-components/dist/components/ConfirmDialog'))
  : () => null;

const PreRegistrationFormContainer = (props) => {
  const {
    countryCode,
    jurisdiction,
    preRegFormHeaderContent,
    preRegFormCTALabel,
    otpFormHeaderContent,
    otpFormCTALabel,
    otpResendTimer,
    commonFooter,
    confirmDialogBannerImage,
    confirmDialogHeaderContent,
    confirmDialogBrandIcon,
    confirmDialogCTAText,
    confirmDialogCTALabel,
    confirmDialogRedirectNotice,
    confirmDialogRedirectTimer,
    confirmDialogRedirectUrl,
    detailsGatheringFields,
    verifyOtpFields,
    brand,
    errorMessages
  } = props;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const promoCode = queryParams.get('promoCode');

  const formRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [mobileNo, setMobileNo] = useState('');
  const [generalFormError, setGeneralFormError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmDialogLoading, setIsConfirmDialogLoading] = useState(false);

  const handleRegister = useCallback(
    async (data) => {
      const { otp: _otp, dob, ...rest } = data;
      const brandId = getBrandId(brand);

      const payload = {
        ...rest,
        dob: dob.replace(/\//g, '-'),
        countryCode,
        jurisdiction,
        brandId,
        ...(promoCode && { promoCode })
      };

      setIsLoading(true);
      const response = await callAPI(restApiUrl + '/register-user', 'POST', payload);
      setIsLoading(false);

      if (!response?.isSuccess) {
        const errorMessage = getErrorMessage(errorMessages, response?.message) || GENERIC_ERROR_MESSAGE;
        setGeneralFormError(errorMessage);
        return;
      }

      setGeneralFormError('');
      setShowConfirmDialog(true);
    },
    [countryCode, jurisdiction, brand, errorMessages, promoCode],
  );

  const handleDetailsGatheringSubmit = useCallback(async () => {
    const { mobileNo: mobile } = formRef.current?.getValues() ?? {};

    if (countryCode && mobile && jurisdiction) {
      formRef.current?.setValue('otp', '');
      setIsLoading(true);
      const response = await callAPI(restApiUrl + '/get-otp', 'POST', {
        countryCode,
        mobileNo: mobile,
        jurisdiction,
      });
      setIsLoading(false);

      if (!response?.isSuccess) {
        const errorMessage = getErrorMessage(errorMessages, response?.message) || GENERIC_ERROR_MESSAGE;
        setGeneralFormError(errorMessage);
        return;
      }

      setGeneralFormError('');
      setMobileNo(mobile);
      setCurrentStep(2);
    }
  }, [countryCode, jurisdiction, errorMessages]);

  const handleVerifyOtpSubmit = useCallback(async () => {
    const { mobileNo: mobile, otp } = formRef.current?.getValues() ?? {};

    if (countryCode && mobile && jurisdiction && otp) {
      setIsLoading(true);
      const response = await callAPI(restApiUrl + '/verify-otp', 'POST', {
        countryCode,
        mobileNo: mobile,
        otp,
        jurisdiction,
      });
      setIsLoading(false);

      if (!response?.isSuccess) {
        const errorMessage = getErrorMessage(errorMessages, response?.message) || GENERIC_ERROR_MESSAGE;
        setGeneralFormError(errorMessage);
        return;
      }

      setGeneralFormError('');
      formRef.current?.requestSubmit();
    }
  }, [countryCode, jurisdiction, errorMessages]);

  const handleConfirm = useCallback(() => {
    setIsConfirmDialogLoading(true);
    window.location.replace(confirmDialogRedirectUrl);
    // Do not set loading to false here, redirect takes a long time to complete
  }, [confirmDialogRedirectUrl]);

  const otpFormHeaderContentNode = useMemo(
    () => (
      <ContentfulRichText
        richTextData={otpFormHeaderContent}
        textReplacements={{
          '{number}': formatMaskedPhoneNumberDisplay(mobileNo),
        }}
      />
    ),
    [otpFormHeaderContent, mobileNo],
  );

  return (
    <>
      <PreRegistrationForm
        ref={formRef}
        id="preRegistrationForm"
        venture={venture}
        currentStep={currentStep}
        isLoading={isLoading}
        otpResendTimer={otpResendTimer}
        detailsGatheringFields={detailsGatheringFields}
        verifyOtpFields={verifyOtpFields}
        clientSideValidations={CLIENT_SIDE_VALIDATIONS}
        onDetailsGatheringSubmit={handleDetailsGatheringSubmit}
        onVerifyOtpSubmit={handleVerifyOtpSubmit}
        onRegister={handleRegister}
        generalError={generalFormError}
        preRegFormHeaderContent={<ContentfulRichText richTextData={preRegFormHeaderContent} />}
        preRegFormCTALabel={preRegFormCTALabel}
        otpFormHeaderContent={otpFormHeaderContentNode}
        otpFormCTALabel={otpFormCTALabel}
        commonFooter={<ContentfulRichText richTextData={commonFooter} />}
        errorMessages={errorMessages}
      />
      <ConfirmDialog
        show={showConfirmDialog}
        venture={venture}
        onConfirm={handleConfirm}
        onRedirect={handleConfirm}
        confirmDialogBannerImage={confirmDialogBannerImage}
        confirmDialogHeaderContent={<ContentfulRichText richTextData={confirmDialogHeaderContent} />}
        confirmDialogBrandIcon={confirmDialogBrandIcon}
        confirmDialogCTAText={confirmDialogCTAText}
        confirmDialogCTALabel={confirmDialogCTALabel}
        confirmDialogCTAIsLoading={isConfirmDialogLoading}
        confirmDialogRedirectNotice={confirmDialogRedirectNotice}
        confirmDialogRedirectTimer={confirmDialogRedirectTimer}
      />
    </>
  );
};

PreRegistrationFormContainer.propTypes = {
  countryCode: PropTypes.string,
  jurisdiction: PropTypes.string,
  detailsGatheringFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      helperText: PropTypes.string,
      required: PropTypes.bool,
      requiredErrorMessage: PropTypes.string,
    }),
  ),
  verifyOtpFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      helperText: PropTypes.string,
      required: PropTypes.bool,
      requiredErrorMessage: PropTypes.string,
    }),
  ),
  preRegFormHeaderContent: PropTypes.object,
  preRegFormCTALabel: PropTypes.string,
  otpFormHeaderContent: PropTypes.object,
  otpFormCTALabel: PropTypes.string,
  otpResendTimer: PropTypes.number,
  commonFooter: PropTypes.object,
  confirmDialogBannerImage: PropTypes.object,
  confirmDialogHeaderContent: PropTypes.object,
  confirmDialogBrandIcon: PropTypes.object,
  confirmDialogCTAText: PropTypes.string,
  confirmDialogCTALabel: PropTypes.string,
  confirmDialogRedirectNotice: PropTypes.string,
  confirmDialogRedirectTimer: PropTypes.number,
  confirmDialogRedirectUrl: PropTypes.string,
  brand: PropTypes.object,
  errorMessages: PropTypes.arrayOf(
    PropTypes.shape({
      baseMessage: PropTypes.string,
      altMessage: PropTypes.string,
    })
  )
};

PreRegistrationFormContainer.defaultProps = {
  countryCode: '+1',
  jurisdiction: 'CA-AB',
};

export default PreRegistrationFormContainer;
